import React, { useRef, useEffect, useCallback, useState, memo } from 'react';
import { Flex, IconButton, Box, Tab } from '@chakra-ui/react';
import { ArrowLeft2, ArrowRight2 } from 'iconsax-react';

const SCROLL_BEHAVIOR = {
 AMOUNT: 0.5, // Scroll by 50% of the container width
 BEHAVIOR: 'smooth',
};

const ARROW_STYLES = {
 base: {
  variant: 'ghost',
  position: 'absolute',
  zIndex: 2,
  borderRadius: '6px',
  w: '48px',
  h: '46px',
  transition:
   'background-color .2s ease-out, opacity .15s ease-out, box-shadow .15s ease-out',
 },
 active: {
  bgColor: 'white',
  opacity: 1,
  pointerEvents: 'auto',
 },
 inactive: {
  bgColor: 'transparent',
  opacity: 0,
  pointerEvents: 'none',
 },
 hover: {
  bg: 'white',
 },
};

const TAB_STYLES = {
 base: {
  bg: 'white',
  pos: 'relative',
  top: '0',
  w: '263px',
  minW: 'fit-content',
  // maxW: '283px',
  h: '40px',
  p: '0 24px',
  lineHeight: '18.78px',
  mb: '5px',
  borderBottom: '1px solid #CCCFD1',
  overflowY: 'visible',
  color: '#5C6066',
  userSelect: 'none',
  zIndex: 1,
 },
 selected: {
  color: '#292B30',
  fontWeight: 600,
  borderBottom: '3px solid #0073BB',
 },
};

const ScrollArrow = memo(({ direction, show, onClick }) => (
 <IconButton
  icon={direction === 'left' ? <ArrowLeft2 /> : <ArrowRight2 />}
  aria-label={`Scroll ${direction}`}
  onClick={onClick}
  {...ARROW_STYLES.base}
  {...(show ? ARROW_STYLES.active : ARROW_STYLES.inactive)}
  _hover={ARROW_STYLES.hover}
  left={direction === 'left' ? 0 : 'auto'}
  right={direction === 'right' ? 0 : 'auto'}
 />
));

ScrollArrow.displayName = 'ScrollArrow';

const TabList = memo(({ fields = [], validPillars = 0, showPillarsTabs = false }) => {
 const tabListRef = useRef(null);
 const [showLeftArrow, setShowLeftArrow] = useState(false);
 const [showRightArrow, setShowRightArrow] = useState(false);

 const checkArrowVisibility = useCallback(() => {
  if (tabListRef.current) {
   const { scrollLeft, scrollWidth, clientWidth } = tabListRef.current;
   setShowLeftArrow(scrollLeft > 0);
   setShowRightArrow(scrollLeft + clientWidth < scrollWidth);
  }
 }, []);

 const scrollTabs = useCallback((direction) => {
  if (tabListRef.current) {
   const scrollAmount = tabListRef.current.clientWidth * SCROLL_BEHAVIOR.AMOUNT;
   tabListRef.current.scrollBy({
    left: direction === 'left' ? -scrollAmount : scrollAmount,
    behavior: SCROLL_BEHAVIOR.BEHAVIOR,
   });
  }
 }, []);

 useEffect(() => {
  const tabListElement = tabListRef.current;

  checkArrowVisibility();
  window.addEventListener('resize', checkArrowVisibility);

  return () => {
   window.removeEventListener('resize', checkArrowVisibility);
  };
 }, [checkArrowVisibility, fields]);

 const visibleTabs = fields
 .slice(0, validPillars)
 .filter(field => !field.invisible && field.pillarName);
 
 return (
  <Flex align='center' position='relative'>
   <ScrollArrow
    direction='left'
    show={showLeftArrow}
    onClick={() => scrollTabs('left')}
   />

   <Box overflow='hidden' flex='1' mx='30px'>
    <Flex
     ref={tabListRef}
     as='div'
     role='tablist'
     p={0}
     overflowX='auto'
     overflowY='hidden'
     css={{
      '&::-webkit-scrollbar': { display: 'none' },
      scrollbarWidth: 'none',
     }}
     onScroll={checkArrowVisibility}
     userSelect='none'
    >
     <Tab role='tab' {...TAB_STYLES.base} _selected={TAB_STYLES.selected}>
      Configurações gerais
     </Tab>

     {showPillarsTabs &&
      visibleTabs.map((field) => (
       <Tab
        key={field.id}
        role='tab'
        {...TAB_STYLES.base}
        _selected={TAB_STYLES.selected}
       >
        {field.pillarName}
       </Tab>
      ))}
    </Flex>
   </Box>

   <ScrollArrow
    direction='right'
    show={showRightArrow}
    onClick={() => scrollTabs('right')}
   />
  </Flex>
 );
});

TabList.displayName = 'TabList';

export default TabList;
